import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { MICRONUTRIENTS, NUTRIENT_INFO, PACKAGE_NUTRIENTS } from '@kraftwerk/shared';
import type { NutrientKey, Nutrients } from '@kraftwerk/shared';
import { toNumber } from './FoodForm.js';

/** Eingabe als Text je Nährstoff — leer heißt unbekannt, nie 0. */
export type NutrientInput = Partial<Record<NutrientKey, string>>;

export function nutrientInput(nutrients: Nutrients | null | undefined): NutrientInput {
  const input: NutrientInput = {};
  for (const [key, value] of Object.entries(nutrients ?? {})) {
    input[key as NutrientKey] = String(value);
  }
  return input;
}

/** Nur ausgefüllte, gültige Felder — `null`, wenn gar nichts eingetragen ist. */
export function nutrientsFromInput(input: NutrientInput): Nutrients | null {
  const result: Nutrients = {};
  for (const [key, text] of Object.entries(input)) {
    const value = toNumber(text ?? '');
    if (value !== null && value >= 0) result[key as NutrientKey] = value;
  }
  return Object.keys(result).length > 0 ? result : null;
}

/**
 * Ballaststoffe, Zucker, Salz (von der Packung) und — aufklappbar —
 * Vitamine und Mineralstoffe. `basis` sagt, worauf sich die Zahlen beziehen
 * („je 100 g“ beim Lebensmittel, „je Portion“ beim Gericht).
 */
export function NutrientFields({
  value,
  onChange,
  basis,
}: {
  value: NutrientInput;
  onChange: (next: NutrientInput) => void;
  basis: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const set = (key: NutrientKey, text: string) => onChange({ ...value, [key]: text });
  const microsFilled = MICRONUTRIENTS.some((key) => (value[key] ?? '').trim() !== '');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {PACKAGE_NUTRIENTS.map((key) => (
          <NutrientField key={key} nutrient={key} value={value[key] ?? ''} onChange={set} />
        ))}
      </div>
      <details open={microsFilled} className="rounded-lg border border-line px-3 py-2">
        <summary className="cursor-pointer text-xs text-ink-muted">
          {t('nutrients.microsHeading', { basis })}
        </summary>
        <div className="mt-2 flex flex-wrap gap-2">
          {MICRONUTRIENTS.map((key) => (
            <NutrientField key={key} nutrient={key} value={value[key] ?? ''} onChange={set} />
          ))}
        </div>
      </details>
    </div>
  );
}

function NutrientField({
  nutrient,
  value,
  onChange,
}: {
  nutrient: NutrientKey;
  value: string;
  onChange: (key: NutrientKey, text: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();
  return (
    <label htmlFor={id} className="flex w-24 flex-col gap-1 text-xs text-ink-muted">
      {t('nutrients.withUnit', {
        name: t(`nutrients.name.${nutrient}`),
        unit: NUTRIENT_INFO[nutrient].unit,
      })}
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(nutrient, event.target.value)}
        className="kw-field text-ink"
      />
    </label>
  );
}
